import { Spin, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import EhrClinicianList, { type EhrClinicianRow } from "../../components/EhrClinicianList/EhrClinicianList.component";
import { useAppSelector } from "../../hooks/redux-hooks";
import { type EhrClinician, membersService } from "../../service/members.service";

/** Re-read cadence while a refresh (a real EHR login, tens of seconds) is running. */
const POLL_MS = 4000;

/**
 * Member Management's host for the shared EHR clinician list: supplies the data and the logged-in transport, which is
 * all that differs from the setup portal.
 */
export default function EhrClinicianInvite({ onInvited }: { onInvited?: () => void }) {
  const isRpaGroup = Boolean(useAppSelector((state) => state.auth.userInfo.group_rpa_enabled));
  const [providers, setProviders] = useState<EhrClinician[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPulling, setIsPulling] = useState(false);
  const [refreshError, setRefreshError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await membersService.getEhrClinicians();
      setProviders(data.has_directory ? data.providers : []);
      setIsPulling(Boolean(data.is_pulling));
    } catch {
      setProviders([]);
      setIsPulling(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isRpaGroup) {
      setLoading(false);
      return;
    }
    load();
  }, [isRpaGroup, load]);

  useEffect(() => {
    if (!isPulling) return;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [isPulling, load]);

  const refresh = async () => {
    setIsPulling(true);
    setRefreshError("");
    try {
      await membersService.refreshEhrClinicians();
    } catch (err) {
      setIsPulling(false);
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setRefreshError(detail || "We couldn't refresh the list just now. Please try again.");
    }
  };

  const invite = async (email: string, row: EhrClinicianRow) => {
    try {
      const res = await membersService.inviteEhrClinician({
        email,
        name: row.name,
        ehr_provider_id: row.ehr_provider_id,
      });
      message.success(res.state === "joined" ? `${email} was added to your team.` : `Invitation sent to ${email}.`);
      onInvited?.();
      return res.state;
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string; email?: string[] } } })?.response?.data;
      throw new Error(data?.email?.[0] || data?.detail || "We couldn't send that invitation. Please try again.");
    }
  };

  if (!isRpaGroup) return null;
  if (loading) {
    return (
      <div style={{ padding: "8px 0 16px" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (providers.length === 0) return null;

  return (
    <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #f0f0f0", flex: "none" }}>
      <EhrClinicianList
        providers={providers}
        onInvite={invite}
        onRefresh={refresh}
        isRefreshing={isPulling}
        refreshError={refreshError}
      />
    </div>
  );
}
